import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import WaveLoader from "../WaveLoader/WaveLoader";
import ProductListHeader from "./ProductListHeader";
import ProductTable from "./ProductTable";
import "./ProductList.css";
import ProductListToolbar from "./ProductListToolbar";
import ChangePreviewModal from "./ChangePreviewModal";
import { toast } from "react-toastify";
import { getUser } from "../../pages/Login/authUtils";
import * as XLSX from "xlsx";

const ProductList = ({
  products,
  fields,
  setProducts,
  redirect,
  deleteApi,
  showEdit = true,
  createPath,
  roleCompare,
  showDelete,
  showUpload,
  showDownload,
  status,
  title,
}) => {
  const navigate = useNavigate();
  const [selectedProducts, setSelectedProducts] = useState([]);
const [dataLoading, setDataLoading] = useState(false); // for API/network calls only
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(50);
  const [totalEntries, setTotalEntries] = useState(0);
  const [dropdownData, setDropdownData] = useState({});
  const [expandedText, setExpandedText] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const hasFetchedRef = useRef(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [fileChanges, setFileChanges] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [originalDataMap, setOriginalDataMap] = useState({});
  

  useEffect(() => {
    if (products?.length) {
      setTotalEntries(products.length);
      const initialFilters = {};
      fields.forEach((f) => (initialFilters[f.fields] = ""));
      setFilters(initialFilters);
    }
  }, [products?.length]);

  useEffect(() => {
    if (!fields.length || Object.keys(dropdownData).length > 0) return;
    if (hasFetchedRef.current || fields.length === 0) return;
    hasFetchedRef.current = true;

    const fetchDropdownData = async () => {
      setDataLoading(true);
      const newDropdownData = {};
      const fetchedApis = new Set();

      for (const field of fields) {
        if (field.api) {
          try {
            const response = await axios.get(field.api);
            newDropdownData[field.fields] = response.data.map((item) => {
              const value = item[field.apiField]; // check this value

              return {
                id: item._id,
                label: value,
              };
            });
          } catch (error) {
            console.error(
              `Error fetching dropdown data for ${field.fields}:`,
              error
            );
          }
          await new Promise((res) => setTimeout(res, 300));
        }
      }

      console.log();

      setDropdownData(newDropdownData);
      setDataLoading(false);
    };

    fetchDropdownData();
  }, [fields]);

  useEffect(() => {
    if (products?.length) {
      setTotalEntries(products.length);
      const initialFilters = {};
      fields.forEach((f) => (initialFilters[f.fields] = ""));
      setFilters(initialFilters);

      // ✅ Set original data map
      const originalMap = {};
      products.forEach(
        (p) => (originalMap[p._id] = JSON.parse(JSON.stringify(p)))
      );
      setOriginalDataMap(originalMap);
    }
  }, [products]);

  const handleRefresh = async () => {
    setDataLoading(true);
    try {
      const response = await axios.get(deleteApi);
      if (response.data) {
        setProducts(response.data);
        setTotalEntries(response.data.length);
      }
    } catch (error) {
      console.error("Refresh failed:", error);
      toast.error("Failed to refresh table data.");
    } finally {
      setDataLoading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setUploading(true);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const XLSX = await import("xlsx");
        const workbook = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        if (jsonData.length > 5000) {
          // 🔁 Upload directly if too large
          const formData = new FormData();
          formData.append("file", file);
          await axios.post(`${deleteApi}/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          toast.success(
            "✅ Uploaded directly (skipped preview due to large size)"
          );
          handleRefresh();
        } else {
          const previewRes = await axios.post(
            `${deleteApi}/upload-preview`,
            jsonData
          );
          setFileChanges(previewRes.data);
          setExcelFile(jsonData);
          setShowPreviewModal(true);
        }
      } catch (err) {
        console.error("Upload error:", err.message || err);
        toast.error("❌ Failed to upload or preview. Please check file.");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleDownloadFiltered = () => {
    if (!filteredProducts.length) {
      toast.warning("No filtered data to download.");
      return;
    }

    const flattenedData = filteredProducts.map((product) => {
      const flattened = {};

      fields.forEach((field) => {
        const val = product[field.fields];

        // 🎯 For ObjectId with array (like fabVendor, UploadingEng, ErectionEng)
        if (field.api && field.multiple && Array.isArray(val)) {
          flattened[field.fields] = val
            .map((item) =>
              typeof item === "object" && item !== null
                ? `${item.name} (${item._id})`
                : item
            )
            .join(", ");
        }

        // 🎯 For single ObjectId fields (like CreatedBy)
        else if (field.api && typeof val === "object" && val !== null) {
          flattened[field.fields] = `${val.name} (${val._id})`;
        }

        // 🎯 If it's an Object with fallback fields
        else if (typeof val === "object" && val !== null) {
          flattened[field.fields] =
            val.name || val.title || JSON.stringify(val);
        }

        // 🎯 For plain arrays (non-object)
        else if (Array.isArray(val)) {
          flattened[field.fields] = val.join(", ");
        } else {
          flattened[field.fields] = val ?? "";
        }
      });

      return flattened;
    });

    const worksheet = XLSX.utils.json_to_sheet(flattenedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Data");

    XLSX.writeFile(workbook, "filtered_inventory.xlsx");
  };

  const formatFieldName = (name) =>
    name.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  const renderField = (value, key, productId) => {
    if (Array.isArray(dropdownData[key])) {
      const match = dropdownData[key].find((opt) => opt.id === value);
      if (match) return match.label;
    }
    if (typeof value === "string") {
      const words = value.trim().split(/\s+/);
      const isExpanded = expandedText[`${productId}-${key}`];
      if (words.length > 50) {
        return (
          <>
            <span>
              {isExpanded ? value : `${words.slice(0, 50).join(" ")}...`}
            </span>
            <button
              onClick={() =>
                setExpandedText((prev) => ({
                  ...prev,
                  [`${productId}-${key}`]: !prev[`${productId}-${key}`],
                }))
              }
            >
              {isExpanded ? "Read Less" : "Read More"}
            </button>
          </>
        );
      }
      return value;
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return (
              item.label || item.name || item.title || JSON.stringify(item)
            );
          }

          // If item is ID (string), try getting from dropdownData
          return dropdownData[key]?.[item] || item;
        })
        .join(", ");
    }

    if (typeof value === "object" && value !== null) {
      const [_, second] =
        Object.entries(value).find(([k]) => k !== "_id") || [];
      return second || JSON.stringify(value);
    }
    return value;
  };

  const handleSelect = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedProducts(
      selectedProducts.length === filteredProducts.length
        ? []
        : filteredProducts.map((p) => p._id)
    );
  };

  const handleEdit = (id) => navigate(`${window.location.pathname}/${id}`);

  const handleDelete = async (id) => {
    if (!showDelete) return toast.error("You don't have permission to delete.");
    if (window.confirm("Delete this product?")) {
      await axios.delete(`${deleteApi}/${id}`);
      window.location.reload();
    }
  };

  const handleDeleteSelected = async () => {
    if (!showDelete) return toast.error("You don't have permission to delete.");
    if (!selectedProducts.length || !window.confirm("Delete selected?")) return;
    setDataLoading(true);
    await Promise.all(
      selectedProducts.map((id) => axios.delete(`${deleteApi}/${id}`))
    );
    window.location.reload();
  };

  const handleCreate = () => createPath && navigate(createPath);
  const handleDownload = async () => {
    const response = await axios.get(`${deleteApi}/download`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "inventory.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleEditupdate = async (id, updateObj) => {
    try {
      await axios.put(`${deleteApi}/${id}`, updateObj);
      toast.success("✅ Updated successfully!");
      handleRefresh();
    } catch (err) {
      console.error("❌ Update failed:", err.response?.data || err.message);
      toast.error("❌ Failed to update entry.");
    }
  };

  const sendBulkLogs = async ({ pendingChanges, originalDataMap, userId }) => {
    if (!pendingChanges || typeof pendingChanges !== "object") {
      console.warn("❗ Invalid pendingChanges:", pendingChanges);
      return;
    }

    const normalize = (val) => {
      if (Array.isArray(val)) {
        return val
          .map((v) => (typeof v === "object" ? JSON.stringify(v) : v))
          .sort();
      }
      if (typeof val === "object" && val !== null) {
        return JSON.stringify(val);
      }
      return typeof val === "number" ? Number(val) : String(val).trim();
    };

    const logs = [];

    for (const id of Object.keys(pendingChanges)) {
      const original = originalDataMap[id];
      const updated = pendingChanges[id];

      if (!original) {
        console.warn(`❗ No original for ${id}`);
        continue;
      }

      console.log("🔍 Sending logs for user:", userId);
      console.log("🔍 Original Data Map:", originalDataMap);
      console.log("🔍 Pending Changes:", pendingChanges);

      const changes = {};
      console.log(`🧪 Checking ${id} - Field-by-field comparison:`);

      for (const key of Object.keys(updated)) {
        const newValNorm = normalize(updated[key]);
        const oldValNorm = normalize(original[key]);

        console.log(
          `  🔎 ${key} => old(normalized):`,
          oldValNorm,
          "new(normalized):",
          newValNorm
        );

        if (newValNorm !== oldValNorm) {
          changes[key] = {
            old: original[key],
            new: updated[key],
          };
        }
      }

      if (Object.keys(changes).length > 0) {
        logs.push({ id, userId, changes });
      }
    }

    if (logs.length === 0) {
      console.log("ℹ️ No changes detected, skipping log submission.");
      return;
    }

    try {
      const res = await axios.post(`${deleteApi}/bulk-logs`, { logs });
      console.log("✅ Logs submitted:", res.data);
      toast.success("📝 Logs saved.");
    } catch (err) {
      console.error(
        "❌ Log submission failed:",
        err.response?.data || err.message
      );
      toast.error("❌ Failed to save logs.");
    }
  };

  const handleBulkUpdate = async () => {
    const updates = Object.entries(pendingChanges); // [ [id, updateObj], ... ]
    const editedRows = updates.map(([id, changes]) => ({
      _id: id,
      ...originalDataMap[id], // start with original row
      ...changes, // override with edited fields
    }));

    let allSuccess = true;

    console.log("🔁 Starting bulk update...");
    console.log("🧾 Pending Changes:", updates);

    await sendBulkLogs({
      pendingChanges, // ✅ correct variable
      originalDataMap,
      userId: currentUserId,
    });

    for (const [id, updateData] of updates) {
      try {
        await axios.put(`${deleteApi}/${id}`, updateData);
        console.log(`✅ Updated ID: ${id}`);
      } catch (err) {
        allSuccess = false;
        console.error(
          `❌ Failed to update ID ${id}:`,
          err.response?.data || err.message
        );
      }
    }

    if (allSuccess) {
      toast.success("✅ All updates saved!");
    } else {
      toast.warning("⚠️ Some updates failed. Check console.");
    }

    setPendingChanges({});
    setTimeout(() => handleRefresh(), 500);
  };

  const sortedProducts = [...products].sort((a, b) =>
    a._id.localeCompare(b._id)
  );

  const user = getUser();
  const currentUserId = user?._id;
  const isSuperAdmin =
    user?.role === "Super Admin" ||
    user?.role === "Site Engineer" ||
    user?.role === "	Project Viewer";

  const accessibleProducts = sortedProducts; // everyone can see all data

  const filteredProducts = accessibleProducts.filter((p) => {
    const matchesFilters = fields.every((f) => {
      const val = p[f.fields];

      if (f.type === "date") {
        const from = filters[`${f.fields}_from`];
        const to = filters[`${f.fields}_to`];

        // If no from/to filter, allow
        if (!from && !to) return true;

        const recordDate = val ? new Date(val) : null;
        if (!recordDate || isNaN(recordDate.getTime())) return false;

        if (from && recordDate < new Date(from)) return false;
        if (to && recordDate > new Date(to)) return false;

        return true;
      }

      const filterVal = filters[f.fields];
      if (!filterVal) return true;

      if (f.type === "ObjectId" || f.api) {
        if (Array.isArray(val)) {
          return val.some((v) => {
            const id = typeof v === "object" && v !== null ? v._id : v;
            return id === filterVal;
          });
        }

        const actualId =
          typeof val === "object" && val !== null ? val._id : val;
        return actualId === filterVal;
      }

      if (f.type === "number") {
        return Number(val) === Number(filterVal);
      }

      return val?.toString().toLowerCase().includes(filterVal.toLowerCase());
    });

    const matchesSearch = searchQuery
      ? fields.some((f) => {
          const val = p[f.fields];
          return val
            ?.toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        })
      : true;

    return matchesFilters && matchesSearch;
  });

  const indexOfLast = currentPage * productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfLast - productsPerPage,
    indexOfLast
  );

  if (dataLoading) return <WaveLoader />;

  return (
    <div className="DynamicListContainer">
      <ProductListHeader
        navigateToAdmin={() => navigate("/")}
        createPath={createPath}
        handleCreate={handleCreate}
        handleDeleteSelected={showDelete ? handleDeleteSelected : null}
        handleDownload={handleDownload}
        handleExcelUpload={handleExcelUpload}
        uploading={uploading}
        hasSelectedRows={selectedProducts.length > 0}
        showDelete={showDelete}
        showUpload={showUpload}
        showDownload={showDownload}
        title={title}
      />

      <ProductListToolbar
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        productsPerPage={productsPerPage}
        setProductsPerPage={setProductsPerPage}
        setCurrentPage={setCurrentPage}
        handleRefresh={handleRefresh}
        totalEntries={totalEntries}
        filters={filters}
        setFilters={setFilters}
        fields={fields}
        dropdownData={dropdownData}
        onDownloadFiltered={handleDownloadFiltered}
        currentPage={currentPage}
        filteredLength={filteredProducts.length}
        paginate={setCurrentPage}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      <ProductTable
        currentProducts={currentProducts}
        fields={fields}
        dropdownData={dropdownData}
        selectedProducts={selectedProducts}
        handleSelect={handleSelect}
        handleSelectAll={handleSelectAll}
        showEdit={showEdit}
        showDelete={showDelete}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        renderField={renderField}
        formatFieldName={formatFieldName}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        user={getUser()}
        handleEditupdate={handleEditupdate}
        pendingChanges={pendingChanges}
        setPendingChanges={setPendingChanges}
        handleBulkUpdate={handleBulkUpdate}
        filteredProducts={filteredProducts}
        status={status}
        api={deleteApi}
        handleRefresh={handleRefresh}
      />

      {showPreviewModal && (
        <ChangePreviewModal
          changes={fileChanges}
          onConfirm={async () => {
            if (!uploadedFile)
              return toast.error("No file available to upload.");

            const formData = new FormData();
            formData.append("file", uploadedFile); // ✅ now sending the original file

            try {
              await axios.post(`${deleteApi}/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 5 * 60 * 1000,
              });
              setShowPreviewModal(false);
              toast.success("✅ Changes successfully uploaded!");
              handleRefresh();
            } catch (err) {
              console.error(
                "Upload failed:",
                err.response?.data || err.message
              );
              toast.error("❌ Upload failed. Please check data format.");
            }
          }}
          onCancel={() => {
            setShowPreviewModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ProductList;
